// Level Names — Table Management screen for level_names (step 8 part 5a): what Appeal One … Four are called in each
// state, for the Tax Savings Appeal Summary's ValueType column. "Every other state" ('*') is the fallback row and is
// always there; a state row overrides it for that state. A blank cell in a state row falls back to "Every other state".
//
// ⛔ EDITING IS DATABASE-ENFORCED: only staff with ntrg_staff.manages_doc_types write; others read. Every write checks
// the affected-row count — an RLS-refused write returns 0 rows and NO error.
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { loadLevelNames, NAMED_LEVELS } from '../utils/levelNames';
import { appealLevelLabel } from '../reports/appealLevels';

const LEVEL_TITLES = { appeal_one: 'Appeal One', appeal_two: 'Appeal Two', appeal_three: 'Appeal Three', appeal_four: 'Appeal Four' };

export default function LevelNamesTable({ c, s }) {
  const [names, setNames] = useState([]);
  const [extraStates, setExtraStates] = useState([]);   // states added on screen, no names saved yet
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [draft, setDraft] = useState('');

  const load = useCallback(async () => {
    const r = await loadLevelNames();
    if (!r.ok) setMsg({ tone: 'error', text: r.error });
    setNames(r.ok ? r.data : []);
    setLoading(false);
  }, []);
  useEffect(() => {
    load();
    supabase.rpc('manages_doc_types').then(({ data, error }) => setCanEdit(!error && data === true), () => setCanEdit(false));
  }, [load]);

  const nameAt = (state, level) => names.find((r) => r.state === state && r.appeal_level === level)?.name || '';
  async function write(q, what) {
    setBusy(true); setMsg(null);
    const { data, error } = await q;
    setBusy(false);
    if (error || !data?.length) { setMsg({ tone: 'error', text: `${what} — not saved: ${error ? error.message : '0 rows changed (no permission?)'}` }); return false; }
    await load();
    return true;
  }
  // Save one cell on leaving it: a name is upserted; a state cell left blank removes that state's own name.
  const saveCell = (state, level, value) => {
    const v = value.trim();
    if (v === nameAt(state, level)) return;
    if (!v) {
      if (state === '*') { setMsg({ tone: 'error', text: 'Every other state needs a name for each level.' }); return; }
      write(supabase.from('level_names').delete().eq('state', state).eq('appeal_level', level).select('id'), `${state} ${LEVEL_TITLES[level]}`);
      return;
    }
    write(supabase.from('level_names').upsert({ state, appeal_level: level, name: v, updated_at: new Date().toISOString() }, { onConflict: 'state,appeal_level' }).select('id'),
      `${state === '*' ? 'Every other state' : state} ${LEVEL_TITLES[level]}`);
  };
  const removeState = (state) => {
    if (!names.some((r) => r.state === state)) { setExtraStates((x) => x.filter((st) => st !== state)); return; }
    if (window.confirm(`Remove ${state}'s level names? It falls back to "Every other state".`)) {
      write(supabase.from('level_names').delete().eq('state', state).select('id'), `Remove ${state}`);
    }
  };
  const addState = () => {
    const st = draft.trim().toUpperCase();
    if (!/^[A-Z]{2}$/.test(st)) { setMsg({ tone: 'error', text: 'A state is two letters, e.g. FL.' }); return; }
    setExtraStates((x) => (x.includes(st) ? x : [...x, st]));
    setDraft('');
  };

  if (loading) return <div style={{ padding: '24px', color: c.muted, fontSize: '13px' }}>Loading…</div>;
  const states = ['*', ...[...new Set([...names.map((r) => r.state).filter((st) => st !== '*'), ...extraStates])].sort()];
  const input = { padding: '4px 6px', fontSize: '12px', border: `1px solid ${c.inputBorder}`, borderRadius: '6px', background: c.input, color: c.text, width: '100%' };
  const small = { ...s.btnGhost, padding: '3px 8px', fontSize: '11px' };

  return (
    <div style={s.card}>
      <div style={s.cardH}>
        <div>
          <div style={s.cardT}>Level Names</div>
          <div style={{ fontSize: '12px', color: c.muted, marginTop: '3px' }}>
            What each appeal level is called in a state — the Tax Savings Appeal Summary's ValueType. A blank state cell uses "Every other state".
            {canEdit ? '' : ' Read-only — only staff with document-type admin rights can edit.'}
          </div>
        </div>
      </div>
      {msg && <div style={{ padding: '10px 18px', fontSize: '12px', fontWeight: '600', color: msg.tone === 'error' ? 'var(--red)' : '#34d399', borderBottom: `1px solid ${c.border}` }}>{msg.text}</div>}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead><tr><th style={s.th}>State</th>{NAMED_LEVELS.map((l) => <th key={l} style={s.th}>{LEVEL_TITLES[l]}</th>)}<th style={s.th} /></tr></thead>
        <tbody>
          {states.map((st) => (
            <tr key={st}>
              <td style={{ ...s.td, fontWeight: 600, whiteSpace: 'nowrap' }}>{st === '*' ? 'Every other state' : st}</td>
              {NAMED_LEVELS.map((l) => (
                <td key={l} style={s.td}>
                  {canEdit ? (
                    <input key={`${st}|${l}|${nameAt(st, l)}`} defaultValue={nameAt(st, l)} disabled={busy} style={input}
                      placeholder={st === '*' ? appealLevelLabel(l) : nameAt('*', l)} aria-label={`${st} ${LEVEL_TITLES[l]}`}
                      onBlur={(e) => saveCell(st, l, e.target.value)} />
                  ) : (nameAt(st, l) || <span style={{ color: c.muted }}>{nameAt('*', l)}</span>)}
                </td>
              ))}
              <td style={{ ...s.td, textAlign: 'right' }}>{canEdit && st !== '*' && <button style={{ ...small, color: '#f87171' }} disabled={busy} onClick={() => removeState(st)}>Remove</button>}</td>
            </tr>
          ))}
          {canEdit && (
            <tr><td colSpan={NAMED_LEVELS.length + 2} style={{ ...s.td, borderBottom: 'none' }}>
              <span style={{ display: 'inline-flex', gap: '8px', alignItems: 'center' }}>
                <input placeholder="State, e.g. FL" value={draft} maxLength={2} onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addState(); }} style={{ ...input, width: '110px' }} />
                <button style={small} disabled={busy || !draft.trim()} onClick={addState}>+ Add State</button>
              </span>
            </td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
